/**
 * `/chat` — V5-01 §5. Two panes: who you talk to, and what was said.
 *
 * The composer is a plain textarea on purpose. A rich editor costs a frame on
 * every keystroke, and the one thing a chat box must never feel is slow;
 * markdown is rendered on display instead.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  CornerUpLeft,
  Hash,
  Info,
  MessageSquare,
  Paperclip,
  Pencil,
  Search,
  Send,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/ui/avatar';
import {
  MessageActions,
  ReactionChips,
  ThreadSummary,
  type MessageActionsProps,
} from './MessageActions';
import { MessageToolbar, useLongPress } from './MessageToolbar';
import { ThreadPane } from './ThreadPane';
import { ComposerToolbar } from './ComposerToolbar';
import { endsGroup, startsGroup } from './grouping';
import { parseMessageBody, renderMentions } from './messageBody';
import {
  activeTypers,
  describeTyping,
  EMPTY_TYPING,
  noteTyping,
  pruneExpired,
  stopTyping,
  type TypingState,
} from './typingState';
import { applyFormat, expandShortcodes, type Format } from './composer';
import { ChannelBrowser } from './ChannelBrowser';
import { CreateTaskFromMessage } from './CreateTaskFromMessage';
import {
  SavedView,
  SearchView,
  ThreadsView,
  type ChatViewKind,
  type JumpTarget,
} from './ChatViews';
import { EmptyState } from '@/components/shared/EmptyState';
import { ERROR_CODES } from '@virtuetasks/types';
import { ApiError } from '@/lib/api';
import { UserProfilePopover } from '@/components/shared/UserProfilePopover';
import { useAuth } from '@/features/auth';
import { useRoom } from '@/hooks/useRealtime';
import { cn } from '@/lib/utils';
import { ConversationHeader } from './ConversationHeader';
import { MessageInfoDialog } from './MessageInfoDialog';
import { NewGroupDialog } from './NewGroupDialog';
import { TaskChip } from './TaskChip';
import { Ticks } from './Ticks';
import {
  chatDownloadLink,
  fetchOlderMessages,
  pingTyping,
  uploadChatFile,
  useChatPeople,
  useConversations,
  useDeleteMessage,
  useMarkRead,
  useMessages,
  useSendMessage,
  useEditMessage,
  useStartConversation,
  pingDelivered,
  useToggleReaction,
  useToggleSaved,
  useTogglePin,
  useFollowedThreads,
  type ChatAttachmentView,
  type ChatMessageView,
  type ConversationView,
} from './api';

/**
 * `@[Paras](64f…)` → `@Paras` — V6-05 §1.
 *
 * The id is in the wire format so a mention names a person rather than a
 * string; the reader never sees it.
 */
/**
 * The classes a code chip or block wears — the §10 follow-up.
 *
 * On the sender's own bubble it cannot borrow `--surface-2`: the bubble sets
 * white text, and a near-white chip under white text is invisible. It gets a
 * darker blue instead and keeps the white — an inset panel, which is what a
 * code block should look like. On a received bubble the surface is right and
 * the text colour is stated rather than inherited, so it cannot drift again.
 */
function codeClasses(onPrimary: boolean): string {
  return onPrimary
    ? 'bg-[var(--code-on-bubble-own)] text-[var(--bubble-own-text)]'
    : 'bg-[var(--surface-2)] text-[var(--text)]';
}

/**
 * Limited markdown (§5): bold, italic, `code`, fenced blocks, and bare links.
 *
 * `onPrimary` says the text sits on the sender's own accent bubble — V10 §10.
 * A link cannot be accent-coloured there, because the background *is* the
 * accent; it renders white, and the **underline** becomes the signal that it is
 * a link, which is what an underline is for when colour cannot say it. Code
 * cannot be surface-coloured there for the same reason.
 *
 * The parsing lives in `messageBody.ts` and is tested there.
 */
function renderBody(raw: string, onPrimary = false): React.ReactNode[] {
  return parseMessageBody(raw).map((segment, index) => {
    switch (segment.kind) {
      case 'bold':
        return <strong key={index}>{segment.value}</strong>;
      case 'italic':
        return <em key={index}>{segment.value}</em>;
      case 'code':
        return (
          <code
            key={index}
            data-testid="chat-code"
            data-on-primary={onPrimary ? 'true' : 'false'}
            className={cn('vt-mono rounded px-1 text-[12px]', codeClasses(onPrimary))}
          >
            {segment.value}
          </code>
        );
      case 'codeBlock':
        return (
          <pre
            key={index}
            data-testid="chat-code-block"
            data-on-primary={onPrimary ? 'true' : 'false'}
            className={cn(
              'vt-mono my-1 overflow-x-auto whitespace-pre-wrap break-words rounded-[6px] px-2 py-1.5 text-[12px]',
              codeClasses(onPrimary),
            )}
          >
            {segment.value}
          </pre>
        );
      case 'link':
        return (
          <a
            key={index}
            href={segment.value}
            target="_blank"
            rel="noreferrer"
            data-testid="chat-link"
            data-on-primary={onPrimary ? 'true' : 'false'}
            className={cn(
              'break-words underline-offset-2',
              onPrimary
                ? 'text-[var(--link-on-bubble-own)] underline decoration-[var(--link-on-bubble-own)]/70 hover:decoration-[var(--link-on-bubble-own)]'
                : 'text-[var(--accent)] hover:underline',
            )}
          >
            {segment.value}
          </a>
        );
      case 'mention':
        return (
          <span
            key={index}
            data-testid="chat-mention"
            data-on-primary={onPrimary ? 'true' : 'false'}
            className={cn(
              'font-medium',
              // Same rule as the link: accent-on-accent is unreadable, so on
              // an own bubble the weight carries it instead of the colour.
              onPrimary ? 'text-[var(--bubble-own-text)]' : 'text-[var(--accent)]',
            )}
          >
            {segment.value}
          </span>
        );
      default:
        return <span key={index}>{segment.value}</span>;
    }
  });
}

function dayLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString();
}

/**
 * A system line: a live task chip (§6) or a membership event (§1).
 *
 * Both are centred and quiet — they are things that happened *to* the
 * conversation, not things somebody said in it.
 */
function SystemMessage({ message }: { message: ChatMessageView }) {
  const state = message.taskRef?.state ?? 'open';

  return (
    <div
      className="my-2 flex justify-center"
      data-testid="chat-system-message"
      data-group-event={message.groupEvent}
      data-chip-state={message.taskRef ? state : undefined}
    >
      <div className="vt-surface flex max-w-[80%] items-center gap-2 px-3 py-1.5 text-[12px]">
        <span className={cn(state === 'deleted' && 'vt-muted')}>{message.body}</span>
        <TaskChip message={message} />
      </div>
    </div>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Download a chat attachment — V10 §5, the same fix as the task attachments.
 *
 * An anchor, not `window.open`: the open happened after an `await`, so the
 * browser no longer counted it as part of the click gesture and the popup
 * blocker discarded it. What saves the file under its own name is the
 * `content-disposition` the server signed into the URL.
 */
async function openAttachment(attachmentId: string): Promise<void> {
  try {
    const { url, fileName } = await chatDownloadLink(attachmentId);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    toast.error(
      err instanceof ApiError && err.code === ERROR_CODES.STORAGE_OBJECT_MISSING
        ? 'That file is unavailable — it is not in this workspace’s storage.'
        : 'Could not get a download link',
    );
  }
}

/** Images inline, everything else as a card you can download (§5). */
function Attachments({
  attachments,
  onPreview,
}: {
  attachments: ChatAttachmentView[];
  onPreview: (attachment: ChatAttachmentView) => void;
}) {
  if (attachments.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid="chat-attachments">
      {attachments.map((attachment) =>
        attachment.isImage && attachment.thumbUrl ? (
          <button
            key={attachment._id}
            type="button"
            data-testid="chat-attachment-image"
            data-file-name={attachment.fileName}
            title={attachment.fileName}
            onClick={() => onPreview(attachment)}
            className="overflow-hidden rounded-[6px] border border-[var(--border)]"
          >
            <img
              /**
               * V10 §9 — the URL comes from the server, signed.
               *
               * It used to be `/api/v1/attachments/:id/thumbnail`, a route
               * that has never existed in this codebase, so every chat image
               * in every room was an `<img>` on a 404.
               */
              src={attachment.thumbUrl}
              alt={attachment.fileName}
              className="size-28 object-cover"
              onError={(e) => {
                // §5(b) — the bytes are in the bucket this workspace used to
                // use. Say so rather than showing a broken glyph.
                (e.currentTarget.parentElement as HTMLElement).dataset.thumbFailed = 'true';
              }}
            />
          </button>
        ) : attachment.isImage ? (
          // An image whose object could not be signed at all — the same
          // pre-bucket-switch case, caught before the request is made.
          <span
            key={attachment._id}
            data-testid="chat-attachment-unavailable"
            data-file-name={attachment.fileName}
            className="vt-surface flex items-center gap-1.5 px-2 py-1 text-[12px] vt-muted"
          >
            <Paperclip className="size-3 shrink-0" />
            <span className="max-w-[180px] truncate">{attachment.fileName}</span>
            <span>— unavailable</span>
          </span>
        ) : (
          <button
            key={attachment._id}
            type="button"
            data-testid="chat-attachment-file"
            data-file-name={attachment.fileName}
            onClick={() => void openAttachment(attachment._id)}
            className="vt-surface flex items-center gap-1.5 px-2 py-1 text-left text-[12px] hover:border-[var(--accent)]"
          >
            <Paperclip className="size-3 shrink-0" />
            <span className="max-w-[180px] truncate">{attachment.fileName}</span>
            <span className="vt-muted">{humanSize(attachment.sizeBytes)}</span>
          </button>
        ),
      )}
    </div>
  );
}

function Message({
  message,
  isMine,
  senderName,
  senderAvatarUrl,
  onDelete,
  onEdit,
  onPreview,
  onReply,
  onInfo,
  onJumpTo,
  actions,
  autoEdit,
  startsNewGroup,
  endsCurrentGroup,
  timeLabel,
}: {
  message: ChatMessageView;
  isMine: boolean;
  senderName?: string;
  senderAvatarUrl?: string;
  onDelete: () => void;
  onEdit: (body: string) => void;
  onPreview: (attachment: ChatAttachmentView) => void;
  onReply: () => void;
  onInfo: () => void;
  onJumpTo: (messageId: string) => void;
  /** V7-04 §3–§5 — react, thread, save, pin, make a task. */
  actions: Omit<MessageActionsProps, 'message'>;
  /** §7 — ↑ on an empty composer asked for this message to be edited. */
  autoEdit?: boolean;
  /** V8-02 §1 — first of a run: carries the avatar and the sender's name. */
  startsNewGroup: boolean;
  /** V8-02 §1 — last of a run: carries the time and, for your own, the ticks. */
  endsCurrentGroup: boolean;
  timeLabel: string;
}) {
  if (message.kind === 'system') return <SystemMessage message={message} />;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  // §2 — touch has no hover, so a long press opens the same actions as a sheet.
  const [sheetOpen, setSheetOpen] = useState(false);
  const longPress = useLongPress(() => setSheetOpen(true));

  // §7 — the composer asked for this one. Seeding the draft here rather than
  // in the effect's dependency list keeps a live edit from being reset by an
  // unrelated re-render.
  useEffect(() => {
    if (!autoEdit) return;
    setDraft(message.body);
    setEditing(true);
  }, [autoEdit, message.body]);
  // Fifteen minutes, matching the window the API enforces.
  const withinEditWindow = Date.now() - new Date(message.createdAt).getTime() < 15 * 60 * 1000;

  if (message.deletedAt) {
    return (
      <div
        className={cn('my-0.5 flex', isMine ? 'justify-end' : 'justify-start')}
        data-testid="chat-message-deleted"
      >
        <span className="px-3 py-1 text-[12px] italic vt-muted">Message deleted</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        /**
         * §1 — avatar column, 8px, bubble block, as one left-aligned unit.
         *
         * The gap used to be `gap-1` plus four hidden-but-laid-out action
         * buttons standing between the avatar and the bubble, which is the
         * hand-width channel in the founder's screenshot. The buttons float
         * now (§2), so this is the only spacing left and it is 8px.
         */
        'group flex items-end gap-2',
        isMine ? 'justify-end' : 'justify-start',
        /**
         * §1's rhythm: 4px inside a group, 16px between groups. The old
         * uniform `my-0.5` gave every message the same air, which is what made
         * a run of five read as five separate remarks.
         */
        startsNewGroup ? 'mt-4' : 'mt-1',
      )}
      data-testid="chat-message"
      data-message-id={message._id}
      data-mine={isMine ? 'true' : 'false'}
      data-group-start={startsNewGroup ? 'true' : 'false'}
      id={`message-${message._id}`}
    >
      {/**
       * §1 — the avatar column, and the end of the dead gutter.
       *
       * The avatar used to sit *inside* the bubble above the name, so every
       * bubble carried its width as padding whether or not it showed one — a
       * ~40px empty channel down the left of every received message. It is a
       * sibling now: 40px wide on the first of a run, and 40px of nothing on
       * the rest, so the bubbles line up without the text being pushed away
       * from the edge.
       */}
      {!isMine && (
        <span className="w-10 shrink-0 self-end pb-0.5" data-testid="chat-avatar-column">
          {startsNewGroup && senderName && (
            <UserProfilePopover userId={message.senderId ?? ''} name={senderName}>
              <span className="inline-flex">
                <UserAvatar
                  id={message.senderId ?? ''}
                  name={senderName}
                  src={senderAvatarUrl}
                  size={32}
                />
              </span>
            </UserProfilePopover>
          )}
        </span>
      )}
      {/**
       * §3 — line 1 is the sender and the time; line 2 is the message.
       *
       * They were one line reading `Gudiya 05:46 p.m.hii`. The name *was* in a
       * `flex` span, which should have been block-level — but it sat inside
       * `UserProfilePopover`, whose trigger is an `inline-flex` button, and an
       * inline box does not start a line. The message text simply flowed on
       * beside it. The name and the bubble are siblings now, in a column, so
       * nothing about the popover's markup can put them back on one line.
       */}
      <div
        className={cn(
          'relative flex min-w-0 max-w-[75%] flex-col md:max-w-[65%]',
          isMine ? 'items-end' : 'items-start',
        )}
      >
        {startsNewGroup && !isMine && senderName && (
          <div
            className="mb-0.5 flex items-baseline gap-1.5 px-1 text-[11px]"
            data-testid="chat-message-header"
          >
            <UserProfilePopover userId={message.senderId ?? ''} name={senderName}>
              {/**
               * The test id sits on an *inner* span deliberately. Radix's
               * `PopoverTrigger asChild` merges its own props onto whatever it
               * is given, so a `data-testid` on the outer element is silently
               * replaced by the trigger's — which is how the first version of
               * this rendered correctly and matched nothing.
               */}
              <span className="font-medium text-[var(--accent)]" data-testid="chat-sender-name">
                {senderName}
              </span>
            </UserProfilePopover>
            <span className="vt-muted" data-testid="chat-message-time">
              {timeLabel}
            </span>
          </div>
        )}

        <div className="relative max-w-full" {...longPress}>
          {/* §2 — over the bubble, never beside it: nothing here is in flow. */}
          <MessageToolbar
            align={isMine ? 'left' : 'right'}
            sheetOpen={sheetOpen}
            onSheetOpenChange={setSheetOpen}
          >
            {/* §7 — reply to anyone's message, including your own. */}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Reply"
              data-testid="chat-reply"
              onClick={onReply}
            >
              <CornerUpLeft />
            </Button>

            {isMine && !editing && (
              <>
                {/* §5 — own messages stay editable for fifteen minutes. Past that the
                  button is gone rather than present-and-refusing. */}
                {withinEditWindow && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Edit message"
                    data-testid="chat-edit"
                    onClick={() => {
                      setDraft(message.body);
                      setEditing(true);
                    }}
                  >
                    <Pencil />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Message info"
                  data-testid="chat-message-info"
                  onClick={onInfo}
                >
                  <Info />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Delete message"
                  data-testid="chat-delete"
                  onClick={onDelete}
                >
                  <Trash2 />
                </Button>
              </>
            )}
            {/* V7-04 §3–§5 — the hover row. Ordered by how often each is used. */}
            <MessageActions message={message} {...actions} />
          </MessageToolbar>

          <div
            className={cn(
              /**
               * §1's geometry: ~65% of the pane on desktop, 75% on a phone
               * where there is no room to be precious about it. The cap lives
               * on the column now so the name line and the bubble are measured
               * against the same width. `min-w-0` and `break-words` below are
               * what keep an unbroken 200-character string from pushing the
               * pane sideways.
               */
              'min-w-0 max-w-full rounded-[10px] px-3 py-1.5 text-[13px] leading-snug',
              /**
               * V10 §10 — the own bubble has its own token now.
               *
               * It was `--accent` directly, and in the dark theme white on
               * that accent is 3.68:1 — below AA for text, and unreachable,
               * because 4.5:1 against it would need a foreground lighter than
               * white. `--bubble-own` is the accent in the light theme and a
               * step down from it in the dark one, so body text and links
               * clear the bar in both.
               */
              isMine ? 'bg-[var(--bubble-own)] text-[var(--bubble-own-text)]' : 'vt-surface',
            )}
            data-testid="chat-bubble"
          >
            {/* §7 — the quoted snippet, tap to scroll to the original. */}
            {message.replyTo && (
              <button
                type="button"
                data-testid="chat-reply-quote"
                className={cn(
                  'mb-1 block w-full truncate rounded-[4px] border-l-2 px-1.5 py-0.5 text-left text-[11px]',
                  isMine
                    ? 'border-white/60 bg-white/15'
                    : 'border-[var(--accent)] bg-[var(--surface-2)]',
                )}
                onClick={() => onJumpTo(message.replyTo!.messageId)}
              >
                <span className="font-medium">{message.replyTo.senderName}</span>{' '}
                <span className="opacity-80">{message.replyTo.snippet}</span>
              </button>
            )}

            {editing ? (
              <input
                className="w-full rounded-[4px] bg-white/90 px-1 text-[13px] text-black"
                aria-label="Edit message"
                data-testid="chat-edit-input"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const next = draft.trim();
                    setEditing(false);
                    if (next && next !== message.body) onEdit(next);
                  }
                  if (e.key === 'Escape') setEditing(false);
                }}
                onBlur={() => setEditing(false)}
              />
            ) : (
              <>
                <span className="whitespace-pre-wrap break-words">{renderBody(message.body, isMine)}</span>
                {message.editedAt && (
                  <span
                    className={cn('ml-1.5 text-[10px]', isMine ? 'opacity-80' : 'vt-muted')}
                    data-testid="chat-edited-marker"
                  >
                    (edited)
                  </span>
                )}
                {/* §4 — ticks on your own messages, clickable for "Message info". */}
                {/**
                 * §1 — own messages carry the time and ticks on the group's *last*
                 * bubble, so a run of four does not repeat the same stamp four
                 * times. The ticks stay on every message for hover, which is where
                 * "Message info" is reached from.
                 */}
                {isMine && endsCurrentGroup && (
                  <span className="ml-1.5 align-middle text-[10px] opacity-70">{timeLabel}</span>
                )}
                {isMine && (
                  <Ticks
                    tick={message.tick}
                    deliveredAt={message.deliveredAt}
                    readAt={message.readAt}
                    onClick={onInfo}
                    /* §4 — this bubble is accent blue; blue ticks on it are invisible. */
                    onAccent
                  />
                )}
              </>
            )}
            <Attachments attachments={message.attachments ?? []} onPreview={onPreview} />

            {/* §4 — a title-only preview for an external link, no images. */}
            {(message.unfurls ?? []).map((unfurl) => (
              <a
                key={unfurl.url}
                href={unfurl.url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="chat-unfurl"
                className={cn(
                  'mt-1 block rounded-[6px] border-l-2 px-1.5 py-1 text-[11px]',
                  isMine
                    ? 'border-white/60 bg-white/15'
                    : 'border-[var(--accent)] bg-[var(--surface-2)]',
                )}
              >
                <span className="block font-medium">{unfurl.title}</span>
                {unfurl.siteName && <span className="block opacity-80">{unfurl.siteName}</span>}
              </a>
            ))}

            {/* §3 — visible without hovering, or nobody would send one. */}
            <ReactionChips message={message} onReact={actions.onReact} />

            {/* §2 — the only way into a thread from the scroll, and the only way
            a reader learns one exists. */}
            {actions.onOpenThread && (
              <ThreadSummary message={message} onOpen={actions.onOpenThread} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * §1 — the sidebar's two sections, in the order they are read.
 *
 * Channels first because they are the rooms somebody chose to be in and
 * scans; direct messages below because they are the ones that find *you*.
 * General is a channel by behaviour even though its stored type is `company`
 * — see the chat README on why the type stayed put — so it is grouped with
 * them rather than left in a section of one.
 */
const SECTIONS: {
  key: string;
  label: string;
  of: (conversation: ConversationView) => boolean;
}[] = [
  {
    key: 'channels',
    label: 'Channels',
    of: (conversation) => conversation.type === 'channel' || conversation.type === 'company',
  },
  {
    key: 'direct',
    label: 'Direct messages',
    of: (conversation) => conversation.type === 'dm' || conversation.type === 'group',
  },
];

/** One row in the sidebar. Extracted so the sectioned list stays readable. */
function ConversationRow({
  conversation,
  isActive,
  onOpen,
}: {
  conversation: ConversationView;
  isActive: boolean;
  onOpen: () => void;
}) {
  const other = conversation.participants[0];
  const isDm = conversation.type === 'dm';
  const label = isDm
    ? (other?.name ?? 'Unknown')
    : conversation.type === 'channel'
      ? // V7-04 §1 — a channel reads as #name wherever it appears, which is how
        // somebody tells one from a group at a glance.
        `#${conversation.name ?? 'channel'}`
      : (conversation.name ?? 'Group');

  /**
   * §8 — the badge on a row is what the sidebar total counts for that row.
   *
   * A channel on mentions-only shows the mentions, not the traffic. Showing
   * the traffic here and counting the mentions in the total would be two
   * numbers that disagree about the same conversation.
   */
  const badge =
    conversation.type === 'channel' && conversation.notifyLevel !== 'all'
      ? (conversation.unreadMentionCount ?? 0)
      : conversation.unreadCount;

  return (
    <button
      type="button"
      data-testid="chat-conversation"
      data-conversation-id={conversation._id}
      data-conversation-type={conversation.type}
      data-name={label}
      data-unread={badge}
      data-muted={conversation.muted ? 'true' : 'false'}
      data-archived={conversation.isArchived ? 'true' : 'false'}
      className={cn(
        'flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-[var(--surface-2)]',
        isActive && 'bg-[var(--surface-2)]',
      )}
      onClick={onOpen}
    >
      {isDm ? (
        <UserAvatar
          id={other?._id ?? ''}
          name={other?.name ?? 'Unknown'}
          src={other?.avatarUrl}
          size={28}
        />
      ) : (
        <span
          className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--surface-2)] text-[13px]"
          aria-hidden
        >
          {conversation.emoji ??
            (conversation.type === 'company' ? '🏢' : conversation.type === 'channel' ? '#' : '👥')}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-[13px]', badge > 0 && 'font-semibold')}>
          {label}
          {conversation.isArchived && <span className="ml-1 text-[10px] vt-muted">(archived)</span>}
        </span>
        <span className="block truncate text-[11px] vt-muted">
          {conversation.lastMessagePreview?.text ?? 'No messages yet'}
        </span>
      </span>
      {badge > 0 && (
        <span
          className="vt-mono rounded-full bg-[var(--accent)] px-1.5 text-[10px] text-white"
          data-testid="chat-unread-badge"
        >
          {badge}
        </span>
      )}
    </button>
  );
}

export function ChatPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeId = params.get('c') ?? undefined;
  /**
   * `?with=<userId>` — arriving from the Message action elsewhere (§5).
   *
   * The conversation is **not** created on open, deliberately: clicking
   * "Message" and changing your mind should not leave an empty thread in the
   * other person's list. It is created when the first message is sent.
   */
  const pendingWith = params.get('with') ?? undefined;

  const { data: conversations = [], isLoading } = useConversations();
  const { data: messages = [] } = useMessages(activeId);
  const send = useSendMessage();
  const remove = useDeleteMessage();
  const markRead = useMarkRead();
  const startConversation = useStartConversation();

  const [search, setSearch] = useState('');
  const { data: people = [] } = useChatPeople(search);
  const [draft, setDraft] = useState('');
  /**
   * V8-02 §3 — typing, per conversation, with deadlines.
   *
   * Was a flat `string[]` rendered behind `{typingFrom && …}`, which is always
   * truthy — so the line was permanently in the document, and an empty array
   * fell through the length checks to "Several people are typing…". See
   * `typingState.ts`.
   */
  const [typing, setTyping] = useState<TypingState>(EMPTY_TYPING);
  const bottom = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const lastTypingPing = useRef(0);

  const edit = useEditMessage();

  /* ── V7-04 state ─────────────────────────────────────── */
  /** §2 — the open thread's root message, or null for "no thread open". */
  const [threadRoot, setThreadRoot] = useState<ChatMessageView | null>(null);
  /** §1 — the browse-and-join dialog. */
  const [browsing, setBrowsing] = useState(false);
  /** §4 — the message a task is being made from. */
  const [taskFrom, setTaskFrom] = useState<string | null>(null);
  /** §2, §5, §6 — which cross-conversation view the left pane is showing. */
  const [view, setView] = useState<ChatViewKind | null>(null);
  /** §7 — the message ↑ asked to edit, cleared as soon as it is honoured. */
  const [editingLast, setEditingLast] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** §6 — set when search was opened from a conversation's header. */
  const [searchScope, setSearchScope] = useState<string | undefined>();

  const react = useToggleReaction();
  const toggleSaved = useToggleSaved();
  const togglePin = useTogglePin();
  const followedThreads = useFollowedThreads();

  const [pending, setPending] = useState<ChatAttachmentView[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<ChatAttachmentView | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * Older pages, kept separately from the live page — §5 infinite scroll.
   *
   * `useMessages` always holds the newest 50 so realtime keeps working; each
   * upward fetch prepends an older page here. Merging them into one query key
   * would mean a new message invalidating and discarding everything already
   * scrolled through.
   */
  const [older, setOlder] = useState<ChatMessageView[]>([]);
  const [reachedStart, setReachedStart] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  /* ── V6-05 state ─────────────────────────────────────── */
  const [newGroup, setNewGroup] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessageView | null>(null);
  const [infoFor, setInfoFor] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const composer = useRef<HTMLTextAreaElement>(null);

  /**
   * §7 — where the "— new messages —" line goes.
   *
   * Captured once when the conversation opens, *before* `markRead` lands.
   * Reading it live would move the divider to the bottom the instant the
   * thread was marked read, which is precisely when somebody is looking at it.
   */
  const [dividerAfter, setDividerAfter] = useState<number>(0);

  // A different conversation is a different history.
  useEffect(() => {
    setOlder([]);
    setReachedStart(false);
    setPending([]);
  }, [activeId]);

  // The thread's own room, joined while it is open (§4).
  useRoom(activeId ? `conversation:${activeId}` : undefined);

  const active = useMemo(
    () => conversations.find((c) => c._id === activeId),
    [conversations, activeId],
  );

  // Opening a thread marks it read; that is what a person means by opening it.
  // Keyed on the id alone: `markRead` is a new object each render, and
  // depending on it would re-mark on every keystroke in the composer.
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  useEffect(() => {
    if (!activeId) return;
    // The count as it was on arrival — see `dividerAfter`.
    const conversation = conversations.find((c) => c._id === activeId);
    setDividerAfter(conversation?.unreadCount ?? 0);
    setReplyTo(null);
    markReadRef.current.mutate(activeId);
    // §4 — and tell the sender their message landed.
    void pingDelivered(activeId);
    // Keyed on the id alone, deliberately: `conversations` changes on every
    // socket event, and re-running this would reset the divider under the
    // reader the moment anybody sent anything.
  }, [activeId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, activeId]);

  /**
   * §3 — expiry runs on a timer rather than on a per-ping `setTimeout`.
   *
   * The old timer was keyed on the state object, and the updater returned the
   * *same reference* when the person was already listed — so React bailed out,
   * the effect never re-ran, and continued typing did not refresh the deadline.
   * A one-second sweep has no such coupling, and `pruneExpired` returns the
   * same object when nothing changed, so a quiet conversation re-renders never.
   */
  useEffect(() => {
    const timer = window.setInterval(() => {
      setTyping((current) => pruneExpired(current, Date.now()));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onTyping = (event: Event): void => {
      const detail = (
        event as CustomEvent<{ conversationId: string; userId: string; name: string }>
      ).detail;
      // Recorded against *its own* conversation, not the one on screen: state
      // is per conversation, so nothing has to be filtered on the way out.
      if (!detail?.conversationId || detail.userId === user?._id) return;
      setTyping((current) => noteTyping(current, detail, Date.now()));
    };
    window.addEventListener('vt:chat:typing', onTyping);
    return () => window.removeEventListener('vt:chat:typing', onTyping);
  }, [user?._id]);

  async function openWith(userId: string): Promise<void> {
    const conversation = (await startConversation.mutateAsync(userId)) as ConversationView;
    setSearch('');
    setParams({ c: conversation._id });
  }

  async function attach(files: FileList | File[]): Promise<void> {
    if (!activeId) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const uploaded = await uploadChatFile(activeId, file);
          setPending((current) => [...current, uploaded]);
        } catch (err) {
          // Quota and file-type refusals arrive here with the server's own
          // words, which are the actionable part.
          toast.error(
            err instanceof Error ? `${file.name}: ${err.message}` : `${file.name} failed`,
          );
        }
      }
    } finally {
      setUploading(false);
    }
  }

  /**
   * §7 — apply a format to whatever is selected in the composer.
   *
   * Reads the selection from the DOM rather than tracking it in state: the
   * textarea is the authority on where the caret is, and mirroring it into
   * React would be one more thing to get out of step. The caret is restored
   * afterwards, because a toolbar that leaves you at the end of the box is a
   * toolbar you use once.
   */
  function format(kind: Format): void {
    const node = composer.current;
    if (!node) return;

    const result = applyFormat(
      { text: draft, start: node.selectionStart, end: node.selectionEnd },
      kind,
    );
    setDraft(result.text);
    requestAnimationFrame(() => {
      node.focus();
      node.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  }

  function submit(): void {
    // §7 — `:thumbsup:` becomes 👍 here rather than on every keystroke:
    // rewriting text under somebody's caret mid-word is how an autocorrect
    // earns its reputation.
    const body = expandShortcodes(draft.trim());
    // §5 — a message is text, attachments, or both.
    if (!body && pending.length === 0) return;

    const attachments = pending.map((a) => a._id);

    if (!activeId && pendingWith) {
      // First message to someone reached through "Message": this is the moment
      // the conversation earns its existence.
      setDraft('');
      setPending([]);
      void (async () => {
        const conversation = (await startConversation.mutateAsync(pendingWith)) as ConversationView;
        await send.mutateAsync({ conversationId: conversation._id, body, attachments });
        setParams({ c: conversation._id });
      })();
      return;
    }

    if (!activeId) return;
    setDraft('');
    setPending([]);
    send.mutate({ conversationId: activeId, body, attachments, replyTo: replyTo?._id });
    setReplyTo(null);
  }

  /** §7 — tap a quote to find what it was quoting. */
  function jumpTo(messageId: string): void {
    const node = document.getElementById(`message-${messageId}`);
    if (!node) {
      toast.info('That message is further back — scroll up to load it.');
      return;
    }
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node.dataset.highlight = 'true';
    setTimeout(() => delete node.dataset.highlight, 1200);
  }

  /**
   * §1 — the @mention typeahead, over this conversation's participants only.
   *
   * A mention is an identity, so the composer inserts `@[Name](id)` and the
   * server resolves ids rather than text: two people share a first name often
   * enough that matching on one would notify the wrong person.
   */
  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null || !active || active.type === 'dm') return [];
    const needle = mentionQuery.toLowerCase();
    return active.participants
      .filter((person) => person.name.toLowerCase().includes(needle))
      .slice(0, 6);
  }, [mentionQuery, active]);

  function insertMention(person: { _id: string; name: string }): void {
    setDraft((current) => current.replace(/@[\w .'-]*$/, `@[${person.name}](${person._id}) `));
    setMentionQuery(null);
    composer.current?.focus();
  }

  /**
   * §7 — `@channel`, the one mention that is not an identity.
   *
   * A plain literal rather than the `@[Name](id)` form, because there is no id
   * to resolve: the server reads the token and treats the room as named. It is
   * offered only where a room *has* a membership worth addressing — a DM has
   * two people and does not need a way to shout at one of them.
   */
  const canMentionChannel = Boolean(
    active && active.type !== 'dm' && '@channel'.startsWith(`@${mentionQuery ?? ''}`),
  );

  function insertChannelMention(): void {
    setDraft((current) => current.replace(/@[\w .'-]*$/, '@channel '));
    setMentionQuery(null);
    composer.current?.focus();
  }

  /** Fetch the page before the oldest message currently on screen. */
  async function loadOlder(): Promise<void> {
    if (!activeId || loadingOlder || reachedStart) return;
    const oldest = older[0] ?? messages[0];
    if (!oldest) return;

    setLoadingOlder(true);
    const container = scroller.current;
    const heightBefore = container?.scrollHeight ?? 0;
    try {
      const page = await fetchOlderMessages(activeId, oldest.createdAt);
      if (page.length === 0) {
        setReachedStart(true);
        return;
      }
      setOlder((current) => [...page, ...current]);
      // Keep the reading position: without this the list jumps as it grows.
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - heightBefore;
      });
    } catch {
      toast.error('Could not load earlier messages');
    } finally {
      setLoadingOlder(false);
    }
  }

  const allMessages = useMemo(() => {
    const seen = new Set(older.map((m) => m._id));
    return [...older, ...messages.filter((m) => !seen.has(m._id))];
  }, [older, messages]);

  /** Names and avatars for group senders, from the conversation itself. */
  const peopleById = useMemo(() => {
    const map = new Map<string, { name: string; avatarUrl?: string }>();
    for (const person of active?.participants ?? []) {
      map.set(person._id, { name: person.name, avatarUrl: person.avatarUrl });
    }
    if (user) map.set(user._id, { name: user.name, avatarUrl: user.avatarUrl });
    return map;
  }, [active, user]);

  /**
   * §3 — "cleared on message send".
   *
   * Somebody whose message has arrived has stopped typing, and waiting out the
   * remaining deadline leaves "Ada is typing…" sitting underneath Ada's
   * message.
   */
  useEffect(() => {
    const newest = messages[messages.length - 1];
    if (!newest || !activeId || newest.senderId === user?._id) return;
    const name = peopleById.get(newest.senderId ?? '')?.name;
    if (!name) return;
    setTyping((current) => stopTyping(current, { conversationId: activeId, name }));
  }, [messages, activeId, user?._id, peopleById]);

  /** Only ever the conversation on screen, and null when nobody is typing. */
  const typingLine = describeTyping(activeId ? activeTypers(typing, activeId, Date.now()) : []);

  /**
   * V8-02 §2 — "socket ack on message receipt AND **on fetch**".
   *
   * The socket ack covers somebody who was online when the message was sent.
   * It cannot cover somebody who was not: the event went out while their client
   * was closed, and nothing replays it. Fetching the list is the other half —
   * the rows carry the latest message of each conversation, so this client
   * demonstrably has them, which is exactly what two grey ticks claim.
   *
   * Only conversations with something unread, so an idle list does not post an
   * acknowledgement per conversation on every refetch.
   */
  useEffect(() => {
    for (const conversation of conversations) {
      if (conversation.unreadCount > 0) void pingDelivered(conversation._id);
    }
  }, [conversations]);

  /* ── V7-04 — what this conversation allows, asked once ────────────── */

  /** §2 — a DM has no threads; the inline reply already does that job. */
  const supportsThreads = active ? active.type !== 'dm' : false;
  /** §5 — where there are admins, pinning follows admin rights. */
  const canPin = active ? (active.type === 'dm' ? true : active.isAdmin) : false;
  /**
   * §1 — the server's own answer, not a rule re-derived here.
   *
   * `canPost` is computed by the same policy call the send path makes, so a
   * composer that is on screen is a composer that will work. Older rows from a
   * cached list may not carry it, and the fallback is "yes" — the send would
   * fail with the reason rather than the UI hiding a control that works.
   */
  const canPost = active?.canPost ?? true;

  /** §2 — the thread the pane has open, kept fresh as messages reload. */
  const liveThreadRoot = useMemo(() => {
    if (!threadRoot) return null;
    return messages.find((m) => m._id === threadRoot._id) ?? threadRoot;
  }, [threadRoot, messages]);

  /** §2 — whether this person follows the open thread, for the bell. */
  const followingOpenThread = useMemo(
    () =>
      Boolean(
        liveThreadRoot &&
        followedThreads.data?.some((thread) => thread.root._id === liveThreadRoot._id),
      ),
    [followedThreads.data, liveThreadRoot],
  );

  /**
   * §2, §5, §6 — go to where a row lives.
   *
   * A hit inside a thread opens the thread rather than the scroll, because the
   * scroll is deliberately not where that message is.
   */
  const jumpToTarget = useCallback(
    (target: JumpTarget) => {
      setView(null);
      setParams({ c: target.conversationId });
      if (target.threadRootId) {
        // The root may not be in the page yet; the pane fetches by id and the
        // placeholder is replaced by `liveThreadRoot` once it arrives.
        setThreadRoot({ _id: target.threadRootId } as ChatMessageView);
      }
    },
    [setParams],
  );

  /**
   * §7 — the id the "— new messages —" line goes above.
   *
   * `dividerAfter` unread messages arrived since this person last read, so the
   * divider belongs before the first of them, counting back from the end.
   */
  const dividerBeforeId = useMemo(() => {
    if (dividerAfter <= 0) return null;
    const index = allMessages.length - dividerAfter;
    return index > 0 ? allMessages[index]._id : null;
  }, [allMessages, dividerAfter]);

  const grouped = useMemo(() => {
    const days: { label: string; items: ChatMessageView[] }[] = [];
    for (const message of allMessages) {
      const label = dayLabel(new Date(message.createdAt));
      const last = days.at(-1);
      if (last?.label === label) last.items.push(message);
      else days.push({ label, items: [message] });
    }
    return days;
  }, [allMessages]);

  return (
    <>
      <PageHeader title="Chat" />

      <div className="flex h-[calc(100dvh-96px)] gap-3 p-4" data-testid="chat-page">
        {/* Left: who */}
        {/* §5 — on a narrow screen the panes stack: the list *is* the screen
            until a conversation is opened, and the thread replaces it. */}
        <aside
          className={cn(
            'vt-surface flex-col overflow-hidden md:flex md:w-[260px] md:shrink-0',
            activeId || pendingWith ? 'hidden md:flex' : 'flex w-full',
          )}
          data-testid="chat-list-pane"
        >
          <div className="flex items-center gap-1.5 border-b border-[var(--border)] p-2">
            <Search className="size-3.5 shrink-0 vt-muted" />
            <Input
              className="h-7 border-0 bg-transparent px-0"
              placeholder="Search people…"
              aria-label="Search people to message"
              data-testid="chat-people-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {/* §1 — the way into a group, beside the way into a DM. */}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="New group"
              title="New group"
              data-testid="new-group"
              onClick={() => setNewGroup(true)}
            >
              <Users />
            </Button>
            {/* V7-04 §1 — and the way into a channel, which is a room you find
                rather than one you are put in. */}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Browse channels"
              title="Browse channels"
              data-testid="browse-channels"
              onClick={() => setBrowsing(true)}
            >
              <Hash />
            </Button>
          </div>

          {/* V7-04 §2, §5, §6 — the cross-conversation views. Tabs rather than
              routes: they are a lens on the left pane, and losing the open
              conversation to look at your saved messages would be a step back
              from what a person had. */}
          <div
            className="flex items-center gap-0.5 border-b border-[var(--border)] px-1 py-1"
            data-testid="chat-view-tabs"
          >
            {(
              [
                ['Chats', null],
                ['Threads', 'threads'],
                ['Saved', 'saved'],
                ['Search', 'search'],
              ] as const
            ).map(([label, kind]) => (
              <button
                key={label}
                type="button"
                data-testid={`chat-view-${label.toLowerCase()}`}
                data-active={view === kind ? 'true' : 'false'}
                className={cn(
                  'rounded-[6px] px-1.5 py-0.5 text-[11px]',
                  view === kind
                    ? 'bg-[var(--accent)]/10 font-medium text-[var(--accent)]'
                    : 'vt-muted hover:bg-[var(--surface-2)]',
                )}
                onClick={() => {
                  // Choosing Search from the tabs means "everywhere"; the
                  // header button is the one that narrows it.
                  if (kind === 'search') setSearchScope(undefined);
                  setView(kind);
                }}
              >
                {label}
                {/* §2 — a followed thread that has moved since you read its
                    room. A dot rather than a count: the number of threads with
                    activity is not a number anybody acts on. */}
                {kind === 'threads' &&
                  followedThreads.data?.some((thread) => thread.hasNewReplies) && (
                    <span
                      className="ml-1 inline-block size-1.5 rounded-full bg-[var(--accent)] align-middle"
                      data-testid="threads-tab-dot"
                      aria-label="New thread replies"
                    />
                  )}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {view === 'threads' && <ThreadsView onJump={jumpToTarget} />}
            {view === 'saved' && <SavedView onJump={jumpToTarget} />}
            {view === 'search' && (
              <SearchView
                query={searchQuery}
                onQueryChange={setSearchQuery}
                conversationId={searchScope}
                conversationLabel={
                  searchScope ? conversations.find((c) => c._id === searchScope)?.name : undefined
                }
                onClearScope={() => setSearchScope(undefined)}
                onJump={jumpToTarget}
              />
            )}

            {view === null && search.trim() ? (
              <div data-testid="chat-people-results">
                {people.map((person) => (
                  <button
                    key={person._id}
                    type="button"
                    data-testid="chat-person"
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-[var(--surface-2)]"
                    onClick={() => void openWith(person._id)}
                  >
                    <UserAvatar
                      id={person._id}
                      name={person.name}
                      src={person.avatarUrl}
                      size={24}
                    />
                    <span className="truncate text-[13px]">{person.name}</span>
                  </button>
                ))}
                {people.length === 0 && (
                  <p className="p-3 text-[12px] vt-muted">Nobody matches that.</p>
                )}
              </div>
            ) : view !== null ? null : isLoading ? (
              <Skeleton className="m-2 h-40" />
            ) : conversations.length === 0 ? (
              <p className="p-3 text-[12px] vt-muted" data-testid="chat-no-conversations">
                No conversations yet. Search for someone above to start one.
              </p>
            ) : (
              <div data-testid="chat-conversation-list">
                {/**
                 * §1 — Channels and Direct messages are separate sections.
                 *
                 * One flat list stops working the moment a person is in a dozen
                 * channels: the DM somebody is waiting on gets pushed under
                 * #random by whichever room happened to speak last. Each header
                 * carries its own unread count, so a section worth opening says
                 * so while it is collapsed under the fold.
                 */}
                {SECTIONS.map(({ key, label, of }) => {
                  const rows = (conversations as ConversationView[]).filter(of);
                  if (rows.length === 0) return null;

                  // Per §8: a channel contributes its mentions, not its traffic.
                  const unread = rows.reduce(
                    (total, row) =>
                      total +
                      (row.notifyLevel === 'all' || row.type !== 'channel'
                        ? row.unreadCount
                        : (row.unreadMentionCount ?? 0)),
                    0,
                  );

                  return (
                    <div key={key} data-testid="chat-section" data-section={key}>
                      <div className="flex items-center gap-1 px-2 pt-2 pb-0.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide vt-muted">
                          {label}
                        </span>
                        {unread > 0 && (
                          <span
                            className="vt-mono rounded-full bg-[var(--accent)] px-1.5 text-[10px] text-white"
                            data-testid="chat-section-unread"
                            data-section={key}
                          >
                            {unread}
                          </span>
                        )}
                      </div>

                      {rows.map((conversation) => (
                        <ConversationRow
                          key={conversation._id}
                          conversation={conversation}
                          isActive={conversation._id === activeId}
                          onOpen={() => setParams({ c: conversation._id })}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* Right: what was said */}
        <section
          className={cn(
            'vt-surface min-w-0 flex-1 flex-col overflow-hidden md:flex',
            activeId || pendingWith ? 'flex' : 'hidden md:flex',
          )}
          data-testid="chat-thread-pane"
        >
          {!activeId && !pendingWith ? (
            <div className="flex flex-1 items-center justify-center">
              <EmptyState
                icon={<MessageSquare className="size-6" />}
                title="No conversation open"
                description="Pick someone on the left, or search for a colleague to start a new conversation."
              />
            </div>
          ) : (
            <>
              {active ? (
                <ConversationHeader
                  conversation={active}
                  onBack={() => setParams({})}
                  onClosed={() => setParams({})}
                  onJumpTo={jumpTo}
                  onSearch={() => {
                    setSearchScope(active._id);
                    setSearchQuery('');
                    setView('search');
                  }}
                />
              ) : (
                <header className="flex items-center gap-2 border-b border-[var(--border)] p-2">
                  <span className="text-[13px] font-medium" data-testid="chat-thread-title">
                    New conversation
                  </span>
                </header>
              )}

              <div
                ref={scroller}
                className="min-h-0 flex-1 overflow-y-auto p-3"
                data-testid="chat-thread"
                onScroll={(e) => {
                  // §5 — infinite scroll upward. Fetching a little before the
                  // very top means the page is usually already there by the
                  // time someone reaches it.
                  if (e.currentTarget.scrollTop < 80) void loadOlder();
                }}
              >
                {loadingOlder && (
                  <p
                    className="py-2 text-center text-[11px] vt-muted"
                    data-testid="chat-loading-older"
                  >
                    Loading earlier messages…
                  </p>
                )}
                {reachedStart && older.length > 0 && (
                  <p className="py-2 text-center text-[11px] vt-muted" data-testid="chat-start">
                    This is the beginning of your conversation.
                  </p>
                )}
                {grouped.map((day) => (
                  <div key={day.label}>
                    <div className="my-2 text-center text-[11px] vt-muted">{day.label}</div>
                    {day.items.map((message, index) => (
                      <div key={message._id}>
                        {/* §7 — where this person stopped reading last time. */}
                        {dividerBeforeId === message._id && (
                          <div
                            className="my-2 flex items-center gap-2"
                            data-testid="chat-unread-divider"
                            data-before-message={message._id}
                          >
                            <span className="h-px flex-1 bg-[var(--accent)]" />
                            <span className="text-[10px] font-medium text-[var(--accent)]">
                              new messages
                            </span>
                            <span className="h-px flex-1 bg-[var(--accent)]" />
                          </div>
                        )}
                        <Message
                          message={message}
                          isMine={message.senderId === user?._id}
                          /**
                           * §1 — grouping is decided against the message
                           * rendered immediately above, which within a day
                           * group is the previous item. A day separator
                           * intervening is handled by `startsGroup` itself,
                           * from the dates, so the first message of each day
                           * starts a group without this having to know.
                           */
                          startsNewGroup={startsGroup(message, day.items[index - 1])}
                          endsCurrentGroup={endsGroup(message, day.items[index + 1])}
                          timeLabel={new Date(message.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          senderName={peopleById.get(message.senderId ?? '')?.name}
                          senderAvatarUrl={peopleById.get(message.senderId ?? '')?.avatarUrl}
                          onDelete={() => remove.mutate(message._id)}
                          onEdit={(body) => edit.mutate({ id: message._id, body })}
                          onPreview={setPreview}
                          onReply={() => setReplyTo(message)}
                          onInfo={() => setInfoFor(message._id)}
                          onJumpTo={jumpTo}
                          autoEdit={editingLast === message._id}
                          actions={{
                            onReact: (emoji) => react.mutate({ messageId: message._id, emoji }),
                            onToggleSaved: () =>
                              toggleSaved.mutate({
                                messageId: message._id,
                                saved: !message.saved,
                              }),
                            // §2 — a DM has no threads, so no thread button.
                            onOpenThread: supportsThreads
                              ? () => setThreadRoot(message)
                              : undefined,
                            // §5 — pins follow admin rights where there are admins.
                            onTogglePin: canPin
                              ? () =>
                                  togglePin.mutate({
                                    messageId: message._id,
                                    pinned: !message.pinnedAt,
                                  })
                              : undefined,
                            // §4 — nothing to make a task from without text.
                            onCreateTask: message.body.trim()
                              ? () => setTaskFrom(message._id)
                              : undefined,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ))}
                <div ref={bottom} />
              </div>

              {/* §3 — `describeTyping` answers null for nobody, so there is no
                  arrangement of this state that renders a sentence about
                  people who are not there. */}
              {typingLine && (
                <div className="px-3 pb-1 text-[11px] vt-muted" data-testid="chat-typing">
                  {typingLine}
                </div>
              )}

              {/* §7 — what you are replying to, above the composer. */}
              {replyTo && (
                <div
                  className="flex items-center gap-2 border-t border-[var(--border)] px-3 py-1.5 text-[12px]"
                  data-testid="chat-reply-banner"
                >
                  <CornerUpLeft className="size-3.5 shrink-0 vt-muted" />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">
                      {peopleById.get(replyTo.senderId ?? '')?.name ?? 'Someone'}
                    </span>{' '}
                    <span className="vt-muted">{renderMentions(replyTo.body).slice(0, 80)}</span>
                  </span>
                  <button
                    type="button"
                    aria-label="Cancel reply"
                    data-testid="chat-reply-cancel"
                    onClick={() => setReplyTo(null)}
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              )}

              {pending.length > 0 && (
                <div
                  className="flex flex-wrap gap-1.5 border-t border-[var(--border)] px-2 pt-2"
                  data-testid="chat-pending-attachments"
                >
                  {pending.map((attachment) => (
                    <span
                      key={attachment._id}
                      className="vt-surface flex items-center gap-1 px-2 py-1 text-[11px]"
                      data-testid="chat-pending-attachment"
                    >
                      <Paperclip className="size-3" />
                      <span className="max-w-[140px] truncate">{attachment.fileName}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${attachment.fileName}`}
                        onClick={() =>
                          setPending((current) => current.filter((a) => a._id !== attachment._id))
                        }
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* V7-04 §1 — an archived or announcements-only channel says so
                  where the composer would have been, rather than offering a box
                  that refuses on send. `canPost` is the server's own answer. */}
              {!canPost && active && (
                <p
                  className="border-t border-[var(--border)] p-3 text-center text-[12px] vt-muted"
                  data-testid="chat-cannot-post"
                  data-reason={active.isArchived ? 'archived' : 'announcements'}
                >
                  {active.isArchived
                    ? 'This channel is archived and read-only. An admin can restore it.'
                    : 'Only channel admins can post here — this channel is set to announcements.'}
                </p>
              )}

              {/* V7-04 §7 — formatting, above the box it formats. */}
              {canPost && (
                <div className="border-t border-[var(--border)] px-2 pt-1">
                  <ComposerToolbar onFormat={format} />
                </div>
              )}

              {/* §1 — gone, not present-and-refusing: the same stance the edit
                  button takes once its fifteen minutes are up. */}
              {canPost && (
                <div
                  className={cn(
                    'relative flex items-end gap-2 p-2 transition-colors',
                    dragging && 'bg-[var(--accent-soft)]',
                  )}
                  data-testid="chat-dropzone"
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    void attach(e.dataTransfer.files);
                  }}
                >
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    hidden
                    data-testid="chat-file-input"
                    onChange={(e) => {
                      if (e.target.files) void attach(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Attach a file"
                    data-testid="chat-attach"
                    disabled={uploading}
                    onClick={() => fileInput.current?.click()}
                  >
                    <Paperclip />
                  </Button>
                  {/* §1 — @mention typeahead over this conversation's people. */}
                  {(mentionCandidates.length > 0 ||
                    (mentionQuery !== null && canMentionChannel)) && (
                    <div
                      className="vt-surface vt-elevated absolute bottom-14 left-12 z-20 w-[220px] p-1"
                      data-testid="mention-menu"
                    >
                      {mentionQuery !== null && canMentionChannel && (
                        <button
                          type="button"
                          data-testid="mention-option"
                          data-name="channel"
                          className="flex w-full items-center gap-2 rounded-[4px] px-1.5 py-1 text-left text-[13px] hover:bg-[var(--surface-2)]"
                          onClick={insertChannelMention}
                        >
                          <span
                            className="grid size-5 shrink-0 place-items-center rounded-full bg-[var(--surface-2)] text-[11px]"
                            aria-hidden
                          >
                            @
                          </span>
                          <span>
                            channel
                            <span className="block text-[10px] vt-muted">
                              Notify everyone here — muted people stay muted
                            </span>
                          </span>
                        </button>
                      )}
                      {mentionCandidates.map((person) => (
                        <button
                          key={person._id}
                          type="button"
                          data-testid="mention-option"
                          data-name={person.name}
                          className="flex w-full items-center gap-2 rounded-[4px] px-1.5 py-1 text-left text-[13px] hover:bg-[var(--surface-2)]"
                          onClick={() => insertMention(person)}
                        >
                          <UserAvatar
                            id={person._id}
                            name={person.name}
                            src={person.avatarUrl}
                            size={20}
                          />
                          {person.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    ref={composer}
                    className="vt-surface max-h-32 min-h-[36px] flex-1 resize-none rounded-[6px] px-2 py-1.5 text-[13px]"
                    placeholder="Write a message…"
                    aria-label="Message"
                    data-testid="chat-composer"
                    rows={1}
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      // An `@` run at the caret opens the typeahead; anything
                      // else closes it.
                      const trailing = /@([\w .'-]*)$/.exec(e.target.value);
                      setMentionQuery(trailing ? trailing[1] : null);
                      // One ping a second is plenty for a 3s indicator.
                      if (activeId && Date.now() - lastTypingPing.current > 1000) {
                        lastTypingPing.current = Date.now();
                        void pingTyping(activeId);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setMentionQuery(null);
                      /**
                       * §7 — ↑ on an empty composer edits your last message.
                       *
                       * Only when empty, and only when the caret is at the very
                       * start: otherwise ↑ is how a person moves through the
                       * three lines they are already writing, and stealing it
                       * would be worse than not having the shortcut.
                       */
                      if (
                        e.key === 'ArrowUp' &&
                        draft.length === 0 &&
                        e.currentTarget.selectionStart === 0
                      ) {
                        const mine = [...messages]
                          .reverse()
                          .find(
                            (message) =>
                              message.senderId === user?._id &&
                              !message.deletedAt &&
                              message.kind === 'user',
                          );
                        if (mine) {
                          e.preventDefault();
                          setEditingLast(mine._id);
                          return;
                        }
                      }
                      if (e.key === 'Enter' && !e.shiftKey) {
                        if (mentionCandidates.length > 0) {
                          e.preventDefault();
                          insertMention(mentionCandidates[0]);
                          return;
                        }
                        e.preventDefault();
                        submit();
                      }
                    }}
                    onPaste={(e) => {
                      // §5 — paste an image straight in. Anything else pasted is
                      // text and belongs in the box.
                      const files = Array.from(e.clipboardData.files).filter((f) =>
                        f.type.startsWith('image/'),
                      );
                      if (files.length > 0) {
                        e.preventDefault();
                        void attach(files);
                      }
                    }}
                  />
                  <Button
                    data-testid="chat-send"
                    disabled={!draft.trim() && pending.length === 0}
                    onClick={submit}
                  >
                    <Send />
                  </Button>
                </div>
              )}
            </>
          )}
        </section>

        {/* V7-04 §2 — the third column. On a phone it replaces the
            conversation rather than sitting beside it: a thread, a
            conversation and a list in 390 points is three unusable panes. */}
        {liveThreadRoot && (
          <ThreadPane
            root={liveThreadRoot}
            conversationId={activeId ?? ''}
            currentUserId={user?._id ?? ''}
            canPost={canPost}
            following={followingOpenThread}
            nameFor={(userId) => peopleById.get(userId ?? '')?.name ?? 'Someone'}
            avatarFor={(userId) => peopleById.get(userId ?? '')?.avatarUrl}
            onClose={() => setThreadRoot(null)}
          />
        )}
      </div>

      {/* V7-04 §1 */}
      <ChannelBrowser
        open={browsing}
        onOpenChange={setBrowsing}
        onOpened={(conversationId) => setParams({ c: conversationId })}
      />

      {/* V7-04 §4 */}
      <CreateTaskFromMessage messageId={taskFrom} onClose={() => setTaskFrom(null)} />

      {newGroup && (
        <NewGroupDialog
          onClose={() => setNewGroup(false)}
          onCreated={(conversation) => {
            setNewGroup(false);
            setParams({ c: conversation._id });
          }}
        />
      )}

      {infoFor && <MessageInfoDialog messageId={infoFor} onClose={() => setInfoFor(null)} />}

      {/* §5 — the same lightbox behaviour attachments use elsewhere: the full
          image over the page, dismissed by clicking anywhere or pressing Esc. */}
      {preview && (
        <div
          role="dialog"
          aria-label={preview.fileName}
          data-testid="chat-lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setPreview(null)}
        >
          <img
            // The 1024px render, or the original when the job has not run.
            src={preview.previewUrl ?? preview.thumbUrl ?? undefined}
            alt={preview.fileName}
            data-testid="chat-lightbox-image"
            className="max-h-full max-w-full rounded-[6px] object-contain"
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close preview"
            data-testid="chat-lightbox-close"
            className="absolute right-4 top-4 text-white"
            onClick={() => setPreview(null)}
          >
            <X />
          </Button>
          <Button
            variant="subtle"
            size="sm"
            data-testid="chat-lightbox-download"
            className="absolute bottom-4"
            onClick={(e) => {
              e.stopPropagation();
              void openAttachment(preview._id);
            }}
          >
            Download {preview.fileName}
          </Button>
        </div>
      )}
    </>
  );
}
